import { cx } from '@apparel/react/cx';
import { useIpInfo } from '@apparel/react/ipInfo/useIpInfo';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormHelperText from '@material-ui/core/FormHelperText';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import { useFormikContext } from 'formik';
import { toLower } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PhoneInput, { PhoneInputProps } from 'react-phone-input-2';

export interface PhoneFieldProps extends Omit<PhoneInputProps, 'value'> {
    name?: string;
    className?: string;
    initialValidate?: boolean;
}

const useStyles = makeStyles(
    () => ({
        root: {},

        field: {
            '& input.form-control': {
                width: '100%',
                height: 40,
                borderRadius: '4px',

                '&:focus-visible': {
                    outlineColor: '#e4002b !important',
                    outline: '-webkit-focus-ring-color auto 1px',
                    outlineStyle: 'auto',
                    outlineWidth: '1px',
                    boxShadow: 'none !important',
                    borderColor: 'none !important',
                },
                '&:hover': {
                    border: '1px solid',
                },
            },

            '& .flag-dropdown': {
                backgroundColor: '#fafafa',
                border: 'none',
                margin: '2px 0px 2px 2px',
                borderRight: '1px solid #DBDBDB !important',
            },
            '& .special-label': {
                top: '-18px',
            },
        },

        loading: {
            padding: '18px 8px',
            width: '100%',
            justifyContent: 'flex-start',
            border: '1px solid #ccc',
            height: 38,
            borderRadius: 3,
        },
    }),
    {
        name: 'PhoneField',
    },
);

export function PhoneField({
    name,
    className,
    placeholder,
    initialValidate = true,
    specialLabel,
    ...rest
}: PhoneFieldProps) {
    const classes = useStyles();
    const formik = useFormikContext<any>();

    const phoneName = useMemo(() => [name, 'phone'].join('.').replace(/^\./, ''), [name]);
    const countryCodeName = useMemo(() => [name, 'country_code'].join('.').replace(/^\./, ''), [name]);

    const phoneField = formik.getFieldMeta<string>(phoneName);
    const getFullPhoneField = formik.getFieldMeta<string>('user.getFullPhone');
    const countryCodeField = formik.getFieldMeta<string>(countryCodeName);
    const phoneMeta = formik.getFieldHelpers<string>(phoneName);
    const countryCodeMeta = formik.getFieldHelpers<string>(countryCodeName);
    const ipInfo = useIpInfo();
    const [country, setCountry] = useState(countryCodeField.value ? '' : toLower(ipInfo.info?.country));

    const handleChange = useCallback(
        (value, { countryCode }) => {
            phoneMeta.setValue(value);
            countryCodeMeta.setValue(countryCode);

            phoneMeta.setTouched(true);
            formik?.setFieldValue(phoneName, value);
        },
        [phoneMeta.setValue, countryCodeMeta.setValue, phoneMeta.setTouched],
    );

    const handleOnMount = (value, country) => {
        if (initialValidate && value && value?.length) {
            handleChange(value, country);
        }
    };

    useEffect(() => {
        if (!countryCodeField.value && ipInfo.info?.country) {
            countryCodeMeta.setValue(toLower(ipInfo.info?.country));
            setCountry(toLower(ipInfo.info?.country));
        }
    }, [ipInfo.info?.country]);

    if (ipInfo.loading) {
        return (
            <div className={classes.loading}>
                <CircularProgress size={18} />
            </div>
        );
    }

    return (
        <>
            {specialLabel ? <Typography variant={'caption'}>{specialLabel}</Typography> : null}
            <PhoneInput
                {...rest}
                containerClass={cx(className, classes.field)}
                countryCodeEditable={false}
                country={country ?? 'us'}
                value={getFullPhoneField?.value ?? phoneField?.value}
                onChange={handleChange}
                specialLabel={specialLabel}
                inputProps={{
                    name: phoneName,
                }}
                onMount={handleOnMount}
            />

            {phoneField.touched && phoneField.error ? (
                <FormHelperText error={true}>{phoneField.error}</FormHelperText>
            ) : null}

            {placeholder ? (
                <Typography variant={'caption'} color={'textSecondary'} style={{ paddingLeft: 3, fontSize: 10 }}>
                    {placeholder}
                </Typography>
            ) : null}
        </>
    );
}
